import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  ShieldCheck,
  ScanLine,
  Check,
  ChevronRight,
  LockKeyhole,
} from "lucide-react";
import "@/styles/landing.css";

const CONTACT =
  "mailto:hello@docverifai.com?subject=Getting%20started%20with%20DocVerifAI";
const PARTIES = [
  {
    name: "Northstar Aero Parts",
    role: "Supplier",
    source: "Purchase order · page 1",
    flagged: false,
  },
  {
    name: "Example Trading Co.",
    role: "Consignee",
    source: "Shipping instructions · page 2",
    flagged: true,
  },
  {
    name: "Meridian Freight",
    role: "Forwarder",
    source: "Shipping instructions · page 1",
    flagged: false,
  },
];

export default function Landing() {
  const [selected, setSelected] = useState(1);
  const party = PARTIES[selected];
  return (
    <div className="lp-root">
      <a className="lp-skip" href="#main">
        Skip to content
      </a>
      <header className="lp-nav">
        <nav className="lp-shell lp-nav-inner" aria-label="Main navigation">
          <Link className="lp-wordmark" to="/" aria-label="DocVerifAI home">
            <img src="/docverifai-dv-logo.png" alt="DocVerifAI" width="2172" height="724" />
          </Link>
          <div className="lp-nav-links">
            <a href="#workflow">How it works</a>
            <a href="#coverage">Coverage</a>
            <a href="#security">Security &amp; data</a>
          </div>
          <div className="lp-nav-actions">
            <Link to="/sign-in">Sign in</Link>
            <a className="lp-button lp-button-small" href={CONTACT}>
              Request a demo
            </a>
          </div>
        </nav>
      </header>
      <main id="main">
        <section className="lp-shell lp-hero">
          <div className="lp-hero-copy">
            <p className="lp-intro">
              <span />
              Document screening for your business
            </p>
            <h1>Take the manual work out of screening.</h1>
            <p className="lp-lede">
              Safely and securely turn your documents into a screening
              report.
            </p>
            <p className="lp-hero-detail">
              DocVerifAI reads your documents, identifies the parties involved
              and screens them against sanctions and watchlists. We prepare the
              report with findings and supporting evidence, so your team can get
              straight to the review.
            </p>
            <div className="lp-actions">
              <a className="lp-button" href={CONTACT}>
                Request a demo
              </a>
              <a className="lp-text-link" href="#sample">
                See an example <ChevronRight size={16} />
              </a>
            </div>
            <p className="lp-small">
              For aerospace, freight, trade, legal due diligence and banking.
            </p>
          </div>
          <div
            className="lp-example"
            id="sample"
            aria-label="Illustrative document screening example"
          >
            <div className="lp-example-heading">
              <span>From paperwork to a screening report</span>
              <span className="lp-sample-label">Sample result</span>
            </div>
            <div className="lp-paper">
              <div className="lp-paper-heading">
                <FileText size={21} />
                <span>
                  Purchase order<strong>PO-2048</strong>
                </span>
                <span className="lp-paper-date">18 September 2026</span>
              </div>
              <div className="lp-paper-parties">
                <div>
                  <span>Supplier</span>
                  <strong>Northstar Aero Parts</strong>
                </div>
                <div>
                  <span>Deliver to</span>
                  <strong>Example Trading Co.</strong>
                </div>
              </div>
              <div className="lp-paper-item">
                <span>Aircraft component assembly</span>
                <span>Qty 02</span>
              </div>
              <div className="lp-paper-foot">
                <span>+ Shipping instructions</span>
                <span>One order</span>
              </div>
            </div>
            <div className="lp-transfer">
              <span />
              <ScanLine size={18} />
              <span />
              Read, connected and screened by DocVerifAI
            </div>
            <div className="lp-review">
              <div className="lp-review-heading">
                <strong>Screening report</strong>
                <span>1 potential match</span>
              </div>
              <div
                className="lp-party-list"
                aria-label="Select a party to see sample findings"
              >
                {PARTIES.map((item, index) => (
                  <button
                    key={item.name}
                    type="button"
                    className={`lp-party ${selected === index ? "lp-party-selected" : ""}`}
                    aria-pressed={selected === index}
                    onClick={() => setSelected(index)}
                  >
                    <span
                      className={`lp-status-dot ${item.flagged ? "lp-status-alert" : ""}`}
                    />
                    <span>
                      <strong>{item.name}</strong>
                      <small>{item.role}</small>
                    </span>
                    <span
                      className={
                        item.flagged ? "lp-status-text" : "lp-status-ok"
                      }
                    >
                      {item.flagged ? "Review match" : "No match"}
                    </span>
                    <ChevronRight size={15} />
                  </button>
                ))}
              </div>
              <div className="lp-finding" aria-live="polite" aria-atomic="true">
                <p className="lp-finding-source">{party.source}</p>
                <strong>
                  {party.flagged
                    ? "Potential denied-party match"
                    : "No matching record in this example."}
                </strong>
                {party.flagged ? (
                  <>
                    <dl className="lp-match-evidence">
                      <div>
                        <dt>Matching record</dt>
                        <dd>Example Trading Company · DEMO-104</dd>
                      </div>
                      <div>
                        <dt>List source</dt>
                        <dd>Sample Export Restrictions Register (fictional)</dd>
                      </div>
                      <div>
                        <dt>Why flagged</dt>
                        <dd>
                          The document name matches an alias on the record,
                          which carries an export restriction.
                        </dd>
                      </div>
                    </dl>
                    <p>
                      Compare company details before deciding whether this is
                      the same business.
                    </p>
                  </>
                ) : (
                  <p>
                    The report keeps this result alongside the party’s role and
                    source document.
                  </p>
                )}
              </div>
            </div>
            <p className="lp-example-note">
              Fictional names and results. A name match does not confirm
              identity.
            </p>
          </div>
        </section>
        <section className="lp-workflow" id="workflow">
          <div className="lp-shell">
            <div className="lp-section-heading">
              <h2>
                You bring the paperwork.
                <br />
                We take it from there.
              </h2>
              <p>
                The supplier on the purchase order. The consignee on the
                shipping instructions. We bring the picture together, so you
                don’t have to.
              </p>
            </div>
            <ol className="lp-steps">
              <li>
                <FileText aria-hidden="true" />
                <span className="lp-step-number">1</span>
                <h3>Send the paperwork</h3>
                <p>
                  Purchase orders, shipping documents, scans or photos. Upload
                  what you have; we’ll read it.
                </p>
              </li>
              <li>
                <ScanLine aria-hidden="true" />
                <span className="lp-step-number">2</span>
                <h3>We connect the evidence</h3>
                <p>
                  We bring together who’s involved and their roles across your
                  documents, then screen them against sanctions and watchlists.
                </p>
              </li>
              <li>
                <ShieldCheck aria-hidden="true" />
                <span className="lp-step-number">3</span>
                <h3>Your report is ready</h3>
                <p>
                  Get the findings with their matching records and document
                  references, ready to review, download and keep.
                </p>
              </li>
            </ol>
          </div>
        </section>
        <section className="lp-shell lp-coverage" id="coverage">
          <div className="lp-coverage-copy">
            <h2>
              We find the matches.
              <br />
              You make the call.
            </h2>
            <p className="lp-lede">Spend your attention where it counts.</p>
            <p>
              A flag is the start of a review. We give you the matching record
              and its source, so you can assess the finding and decide what
              happens next.
            </p>
            <a
              className="lp-text-link"
              href="mailto:hello@docverifai.com?subject=DocVerifAI%20screening%20coverage"
            >
              Ask which lists we check <ChevronRight size={16} />
            </a>
          </div>
          <dl className="lp-coverage-list">
            {[
              ["Sanctions", "Government sanctions lists and designations."],
              [
                "Denied parties",
                "Export restrictions, procurement debarment and banned suppliers.",
              ],
              [
                "Politically exposed persons",
                "PEPs and their close associates.",
              ],
              [
                "Enforcement",
                "Regulatory actions, convictions and wanted notices.",
              ],
            ].map(([title, description]) => (
              <div key={title}>
                <dt>
                  <Check size={17} aria-hidden="true" />
                  {title}
                </dt>
                <dd>{description}</dd>
              </div>
            ))}
          </dl>
        </section>
        <section className="lp-security" id="security">
          <div className="lp-shell lp-security-inner">
            <div className="lp-security-heading">
              <LockKeyhole size={30} strokeWidth={1.5} aria-hidden="true" />
              <h2>
                Your documents deserve
                <br />
                clear answers.
              </h2>
              <p>
                Your paperwork contains sensitive business information. We’ll
                explain how it’s handled and answer your questions before you
                start.
              </p>
              <Link className="lp-text-link" to="/privacy">
                Read our privacy policy <ChevronRight size={16} />
              </Link>
            </div>
            <div className="lp-security-details">
              <div>
                <h3>Account access</h3>
                <p>
                  Document uploads and screening results require a signed-in
                  account.
                </p>
              </div>
              <div>
                <h3>How your data is used</h3>
                <p>
                  We use external AI services to read your documents and
                  screening services to check names. We store documents and
                  results so you can return to them.
                </p>
              </div>
              <div>
                <h3>Your data questions</h3>
                <p>
                  We’ll answer your questions about storage and retention. You
                  can request access, corrections or deletion of your personal
                  information.
                </p>
              </div>
              <a
                className="lp-text-link"
                href="mailto:hello@docverifai.com?subject=DocVerifAI%20security%20and%20data%20questions"
              >
                Talk to us about your data <ChevronRight size={16} />
              </a>
            </div>
          </div>
        </section>
        <section className="lp-shell lp-company">
          <div>
            <p className="lp-intro">About DocVerifAI</p>
            <h2>
              Less paperwork.
              <br />
              More time for business.
            </h2>
          </div>
          <div>
            <p>
              DocVerifAI is a service from Verifai Technologies. We take the
              repetitive work out of document screening, from reading the
              paperwork to preparing the report.
            </p>
            <p>
              Whether you’re supplying aircraft parts, arranging freight or
              checking a new business relationship, we help you get the checks
              done and get on with your day.
            </p>
          </div>
        </section>
        <section className="lp-shell lp-contact">
          <div>
            <h2>Put the paperwork on our desk.</h2>
            <p>
              See the report your team could be reviewing instead of building.
              We’ll walk you through DocVerifAI and help you get started.
            </p>
          </div>
          <a className="lp-button" href={CONTACT}>
            Request a demo
          </a>
        </section>
      </main>
      <footer className="lp-shell lp-footer">
        <div>
          <Link className="lp-wordmark" to="/">
            <img src="/docverifai-dv-logo.png" alt="DocVerifAI" width="2172" height="724" />
          </Link>
          <p>A product of Verifai Technologies</p>
        </div>
        <a href="mailto:hello@docverifai.com">hello@docverifai.com</a>
        <nav aria-label="Legal">
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
        </nav>
      </footer>
    </div>
  );
}
